import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { AssistantClientRow } from '@cumpli/shared';
import { Pill, friendlyEstado } from '../../ui';
import { CardHeader, CardShell } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';
import { asEstado, riesgoLabel, riesgoToneOf } from './labels';

/** One client row → deep links into the analyst view (`expedienteId` is always plain). */
function ClientRow({ row }: { row: AssistantClientRow }) {
  const estadoKey = isRedacted(row.estado) ? null : asEstado(row.estado);
  const friendly = estadoKey ? friendlyEstado(estadoKey) : null;
  return (
    <Link
      to={`/revision/${row.expedienteId}`}
      className="flex flex-col gap-1.5 rounded-lg bg-canvas px-2.5 py-2 transition-colors hover:bg-line/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {/* Name gets its own full-width line so it's never squeezed by the pills
          (the card can be as narrow as the ⌘K drawer). */}
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
          <MaybeRedacted value={row.nombre} />
        </span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" strokeWidth={2} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Pill tone={friendly?.tone ?? 'slate'}>
          {friendly ? friendly.label : <MaybeRedacted value={row.estado} />}
        </Pill>
        <Pill tone={isRedacted(row.riesgo) ? 'slate' : riesgoToneOf(row.riesgo)}>
          <MaybeRedacted value={row.riesgo} render={riesgoLabel} />
        </Pill>
      </div>
    </Link>
  );
}

/**
 * Shared renderer for `find_client` and `list_clients` — a count line plus one
 * tappable row per client (name + estado + risk). `variant` only changes the
 * header copy (search results vs recent clients).
 */
export function ClientsCard({
  rows,
  count,
  variant,
}: {
  rows: AssistantClientRow[];
  count: number;
  variant: 'find' | 'list';
}) {
  const header =
    count === 0
      ? variant === 'find'
        ? 'Sin clientes que coincidan'
        : 'Sin clientes registrados'
      : variant === 'find'
      ? `${count} ${count === 1 ? 'cliente encontrado' : 'clientes encontrados'}`
      : `${count} ${count === 1 ? 'cliente reciente' : 'clientes recientes'}`;

  return (
    <CardShell>
      <CardHeader title={header} label="Clientes" />
      {rows.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {rows.map((row) => (
            <ClientRow key={row.expedienteId} row={row} />
          ))}
        </div>
      )}
    </CardShell>
  );
}
